import { useFormContext } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { formSchema } from "./TripAuditInspection";
import z from "zod";
import Image from "next/image";
import CustomDateRangeSelector from "../../ui/shared/CustomDateRangeSelector";
import { format } from "date-fns";
import { logError } from "../../../lib/sentry";
import apiConfig from "../../../lib/config";
type FormData = z.infer<typeof formSchema>;
type Country = {
  Country: string; // country name
  DialCode: number; // comes as number (1, 44, 880, etc.)
  FlagSVG: string; // URL to SVG flag
  ISO2: string;
};
const OwnerDriverInfo = ({ onNext }: { onNext: () => void }) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<FormData>();

  const sameAsOwner = watch("driver.sameAsOwner");
  const ownerFullName = watch("owner.fullName");
  const ownerEmail = watch("owner.email");
  const ownerAddress = watch("owner.address");
  // For owner
  const [ownerSearch, setOwnerSearch] = useState("");

  // For driver
  const [driverSearch, setDriverSearch] = useState("");
  const [countries, setCountries] = useState<Country[] | null>(null);

  // Separate states for owner
  const ownerDropdownRef = useRef<HTMLDivElement>(null);
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);
  const [ownerCountryCode, setOwnerCountryCode] = useState("+1");
  const [ownerFlag, setOwnerFlag] = useState<string>(
    "https://hatscripts.github.io/circle-flags/flags/ca.svg",
  );
  const [ownerPhoneNumber, setOwnerPhoneNumber] = useState("");
  // Separate states for driver
  const driverDropdownRef = useRef<HTMLDivElement>(null);
  const [isDriverDropdownOpen, setIsDriverDropdownOpen] = useState(false);
  const [driverCountryCode, setDriverCountryCode] = useState("+1");
  const [driverFlag, setDriverFlag] = useState<string>(
    "https://hatscripts.github.io/circle-flags/flags/ca.svg",
  );
  const [driverPhoneNumber, setDriverPhoneNumber] = useState("");

  const baseUrl = `${apiConfig.api.baseUrl}`;

  const selectOwnerCountryCode = (data: Country) => {
    const codeWithPlus = `+${data.DialCode}`;
    setOwnerCountryCode(codeWithPlus);
    setOwnerFlag(data?.FlagSVG);
    setIsOwnerDropdownOpen(false);
    setValue("owner.phone", `${codeWithPlus}${ownerPhoneNumber}`);
  };

  const selectDriverCountryCode = (data: Country) => {
    const codeWithPlus = `+${data.DialCode}`;
    setDriverCountryCode(codeWithPlus);
    setDriverFlag(data.FlagSVG);
    setIsDriverDropdownOpen(false);
    setValue("driver.phone", `${codeWithPlus}${driverPhoneNumber}`);
  };

  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await fetch(`${baseUrl}/get_country_codes`);
        const data = await res.json();
        setCountries(data); // assuming the array is directly in the response
        // If response is { data: [...] }, use: setCountries(data.data);
      } catch (error) {
        logError(error, {
          feature: "Failed to country codes",
          // action: "Failed manual add damage ",
          extra: {
            endpoint: `/api/get_country_codes`,
          },
        });
      }
    };
    getCountries();
  }, [baseUrl]);
  const displayedDriverCountryCode = sameAsOwner
    ? ownerCountryCode
    : driverCountryCode;
  const displayedDriverFlag = sameAsOwner ? ownerFlag : driverFlag;
  const displayedDriverPhoneNumber = sameAsOwner
    ? ownerPhoneNumber
    : driverPhoneNumber;
  // Sync driver fields with owner fields when sameAsOwner is true
  // Clear them when sameAsOwner becomes false
  useEffect(() => {
    if (sameAsOwner) {
      // Copy from owner → driver
      setValue("driver.fullName", ownerFullName || "");
      setValue("driver.email", ownerEmail || "");
      setValue("driver.address", ownerAddress || "");
      setValue("driver.phone", `${ownerCountryCode}${ownerPhoneNumber}`);
    } else {
      // Clear driver fields when unchecked
      setValue("driver.fullName", "");
      setValue("driver.email", "");
      setValue("driver.address", "");
      setValue("driver.phone", "");
    }
  }, [
    sameAsOwner,
    ownerFullName,
    ownerEmail,
    ownerAddress,
    ownerCountryCode,
    ownerPhoneNumber,
    setValue,
  ]);
  const handleOwnerPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Allow only digits
    setOwnerPhoneNumber(value);
    setValue("owner.phone", `${ownerCountryCode}${value}`);
  };

  const handleDriverPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Allow only digits
    setDriverPhoneNumber(value);
    setValue("driver.phone", `${driverCountryCode}${value}`);
  };
  const filterCountries = (countries: Country[] | null, search: string) => {
    if (!countries) return [];
    if (!search.trim()) return countries;

    const term = search.toLowerCase().trim();
    return countries.filter(
      (c) =>
        c.Country.toLowerCase().includes(term) ||
        String(c.DialCode).includes(term) ||
        c.ISO2.toLowerCase().includes(term),
    );
  };
  const handleDateChange = (start: Date | null, end: Date | null) => {
    if (start) {
      const startStr = format(start, "yyyy-MM-dd");
      setValue("trip.startDate", startStr, { shouldValidate: true });
    }
    if (end) {
      const endStr = format(end, "yyyy-MM-dd");
      setValue("trip.endDate", endStr, { shouldValidate: true });
      console.log("Saved end date:", endStr);
    }
  };
  return (
    <div className="">
      <div className="text-center mb-5 md:mb-10">
        <h2 className="text-[18px] md:text-[22px] font-bold text-[#303030]">
          Owner & Driver Info
        </h2>
        <p className="text-[14px] leading-5 text-[#6F6464]">
          Provide car owner and driver information
        </p>
      </div>
      <h2 className="text-[18px] font-semibold text-[#303030] mb-[10px]">
        Car Owner
      </h2>
      <div className="mb-5">
        <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px]">
          Full name
        </label>
        <input
          {...register("owner.fullName")}
          placeholder="Enter name"
          className="p-[10px] w-full bg-[#F6F6F6] rounded-[6px] text-[#04082C]  focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
        />
        {errors.owner?.fullName && (
          <p className="text-red-500 text-sm">
            {errors.owner.fullName.message}
          </p>
        )}
      </div>
      <div className="mb-5 flex flex-col md:flex-row gap-[10px] md:items-center">
        <div className="flex-1">
          <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
            Email
          </label>
          <input
            {...register("owner.email")}
            placeholder="Enter email"
            className="p-[10px] w-full bg-[#F6F6F6] rounded-[6px] text-[#04082C]  focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
          />
          {errors.owner?.email && (
            <p className="text-red-500 text-sm">{errors.owner.email.message}</p>
          )}
        </div>
        <div className="flex-1">
          <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
            Phone
          </label>
          <div className="flex h-[45px] w-full rounded-md">
            <div className="relative h-full" ref={ownerDropdownRef}>
              <button
                type="button"
                onClick={() => setIsOwnerDropdownOpen(!isOwnerDropdownOpen)}
                className="h-full flex items-center rounded-md bg-[#F7F7F7] px-3 border-r border-[#e5e5e5]"
              >
                <Image
                  src={ownerFlag}
                  alt="Flag"
                  className="mr-2 h-[20px] w-[24px] rounded-[6px]"
                  width={50}
                  height={50}
                />
                <span className="text-[14px] text-[#04082C]">
                  {ownerCountryCode}
                </span>
                <div className="w-[25px] ml-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M6.99927 9.68884C6.93099 9.68884 6.86778 9.67687 6.80963 9.65295C6.75149 9.62901 6.69755 9.59125 6.64781 9.53965L4.05905 6.95087C3.97993 6.87363 3.93944 6.77929 3.93756 6.66786C3.93569 6.55643 3.97701 6.45846 4.06152 6.37396C4.14416 6.29132 4.24026 6.25 4.34982 6.25C4.45938 6.25 4.55593 6.29084 4.63947 6.37251L6.99781 8.73366L9.35896 6.37251C9.43689 6.29458 9.53158 6.25468 9.64301 6.2528C9.75444 6.25093 9.85241 6.29132 9.93691 6.37396C10.0195 6.45846 10.0609 6.5555 10.0609 6.66506C10.0609 6.77462 10.0204 6.86989 9.93937 6.95087L7.35061 9.53965C7.29901 9.59125 7.24462 9.62901 7.18746 9.65295C7.13028 9.67687 7.06755 9.68884 6.99927 9.68884Z"
                      fill="#6F6464"
                    />
                  </svg>
                </div>
              </button>
              {isOwnerDropdownOpen && (
                <div className="absolute z-50 mt-1 w-[240px] bg-white rounded-lg shadow-xl border border-[#e5e5e5] max-h-[320px] flex flex-col overflow-hidden">
                  {/* Search input */}
                  <div className=" p-2.5 border-b sticky  top-0 bg-white z-10">
                    <input
                      type="text"
                      value={ownerSearch}
                      onChange={(e) => setOwnerSearch(e.target.value)}
                      placeholder="Search country or code..."
                      className="w-full px-3 py-2 text-sm border text-[#303030] placeholder:text-[#999]  border-gray-300 rounded-md focus:outline-none"
                      autoFocus
                    />
                  </div>

                  {/* Scrollable list */}
                  <div
                    className="overflow-y-auto flex-1 custom-scrollbar"
                    onWheel={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchMove={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {filterCountries(countries, ownerSearch).map((country) => (
                      <div
                        key={country.ISO2}
                        className={`flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors ${
                          `+${country.DialCode}` === ownerCountryCode
                            ? "bg-blue-100"
                            : ""
                        }`}
                        onClick={() => {
                          selectOwnerCountryCode(country);
                          setOwnerSearch(""); // optional: clear search after select
                        }}
                      >
                        <Image
                          src={country.FlagSVG}
                          alt={country.Country}
                          width={24}
                          height={20}
                          className="rounded object-cover"
                        />
                        <span className="flex-1 text-sm text-gray-800">
                          {getShortCountryName(country.Country)}
                        </span>
                        <span className="text-sm text-gray-500">
                          +{country.DialCode}
                        </span>
                      </div>
                    ))}

                    {filterCountries(countries, ownerSearch).length === 0 && (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No countries found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <input
              maxLength={17}
              placeholder="Phone number"
              value={ownerPhoneNumber}
              onChange={handleOwnerPhoneChange}
              className="flex-1 h-[45px] rounded-r  bg-[#F7F7F7] px-4 font-openSans text-[14px] text-[#04082C] outline-none placeholder:text-[#7D7D7D]"
            />
          </div>
          {errors.owner?.phone && (
            <p className="text-red-500 text-sm">{errors.owner.phone.message}</p>
          )}
        </div>
      </div>
      <div className="mb-5">
        <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px]">
          Current address (Optional)
        </label>
        <input
          {...register("owner.address")}
          placeholder="Enter address"
          className="p-[10px] w-full bg-[#F6F6F6] text-[#04082C]  rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
        />
        {errors.owner?.address && (
          <p className="text-red-500 text-sm">{errors.owner.address.message}</p>
        )}
      </div>

      <label className="flex justify-end text-[#6F6464] text-[12px] font-medium">
        <input
          type="checkbox"
          {...register("driver.sameAsOwner")}
          className="mr-2"
        />
        Same person
      </label>

      <h2 className="text-[18px] font-semibold text-[#303030] mb-[10px]">
        Car Driver
      </h2>
      <div className="mb-5">
        <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px]">
          Full name
        </label>
        <input
          {...register("driver.fullName")}
          placeholder="Enter name"
          className="p-[10px] w-full text-[#04082C]  bg-[#F6F6F6] rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
          readOnly={sameAsOwner}
          value={sameAsOwner ? ownerFullName || "" : undefined}
        />
        {errors.driver?.fullName && (
          <p className="text-red-500 text-sm">
            {errors.driver.fullName.message}
          </p>
        )}
      </div>
      <div className="mb-5 flex flex-col md:flex-row gap-[10px] md:items-center">
        <div className="flex-1">
          <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
            Email
          </label>
          <input
            {...register("driver.email")}
            placeholder="Enter email"
            className="p-[10px] w-full bg-[#F6F6F6] text-[#04082C]  rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
            readOnly={sameAsOwner}
            value={sameAsOwner ? ownerEmail || "" : undefined}
          />
          {errors.driver?.email && (
            <p className="text-red-500 text-sm">
              {errors.driver.email.message}
            </p>
          )}
        </div>
        <div className="flex-1">
          <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
            Phone
          </label>
          <div className="flex h-[45px] w-full rounded-md">
            <div className="relative h-full" ref={driverDropdownRef}>
              <button
                type="button"
                onClick={() => setIsDriverDropdownOpen(!isDriverDropdownOpen)}
                className="h-full flex items-center rounded-l bg-[#F7F7F7] px-3 border-r border-[#e5e5e5]"
                disabled={sameAsOwner}
              >
                <Image
                  src={displayedDriverFlag}
                  alt="Flag"
                  className="mr-2 h-[20px] w-[24px] rounded-[6px]"
                  width={50}
                  height={50}
                />
                <span className="text-[14px] text-[#04082C]">
                  {displayedDriverCountryCode}
                </span>
                <div className="w-[25px] ml-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    {/* your existing arrow svg */}
                  </svg>
                </div>
              </button>

              {isDriverDropdownOpen && !sameAsOwner && (
                <div className="absolute z-50 mt-1 w-[240px] bg-white rounded-lg shadow-xl border border-[#e5e5e5] max-h-[320px] flex flex-col overflow-hidden">
                  {/* Search input */}
                  <div className="p-2.5 border-b sticky top-0 bg-white z-10">
                    <input
                      type="text"
                      value={driverSearch}
                      onChange={(e) => setDriverSearch(e.target.value)}
                      placeholder="Search country or code..."
                      className="w-full text-[#04082C] placeholder:text-[#999]  px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-100"
                      autoFocus
                    />
                  </div>

                  {/* Scrollable list */}
                  <div
                    className="overflow-y-auto flex-1 custom-scrollbar overscroll-contain"
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                  >
                    {filterCountries(countries, driverSearch).map((country) => (
                      <div
                        key={country.ISO2}
                        className={`flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors ${
                          `+${country.DialCode}` === driverCountryCode
                            ? "bg-blue-100"
                            : ""
                        }`}
                        onClick={() => {
                          selectDriverCountryCode(country);
                          setDriverSearch(""); // clear search after select — good UX
                        }}
                      >
                        <Image
                          src={country.FlagSVG}
                          alt={country.Country}
                          width={24}
                          height={20}
                          className="rounded object-cover"
                        />
                        <span className="flex-1 text-sm text-gray-800">
                          {getShortCountryName(country.Country)}
                        </span>
                        <span className="text-sm text-gray-500">
                          +{country.DialCode}
                        </span>
                      </div>
                    ))}

                    {filterCountries(countries, driverSearch).length === 0 && (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No countries found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <input
              maxLength={17}
              placeholder="Phone number"
              value={displayedDriverPhoneNumber}
              onChange={sameAsOwner ? undefined : handleDriverPhoneChange}
              readOnly={sameAsOwner}
              className="flex-1 h-[45px]  rounded-r bg-[#F7F7F7] px-4 font-openSans text-[14px] text-[#04082C] outline-none placeholder:text-[#7D7D7D]"
            />
          </div>

          {errors.driver?.phone && (
            <p className="text-red-500 text-sm">
              {errors.driver.phone.message}
            </p>
          )}
        </div>
      </div>
      <div className="mb-5">
        <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px]">
          Driving license (Optional)
        </label>
        <input
          {...register("driver.license")}
          placeholder="Enter license number"
          className="p-[10px] w-full bg-[#F6F6F6] text-[#04082C]  rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
        />
        {errors.driver?.license && (
          <p className="text-red-500 text-sm">
            {errors.driver.license.message}
          </p>
        )}
      </div>
      <div className="mb-5">
        <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px]">
          Current address (Optional)
        </label>
        <input
          {...register("driver.address")}
          placeholder="Enter address"
          className="p-[10px] w-full text-[#04082C]  bg-[#F6F6F6] rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
          readOnly={sameAsOwner}
          value={sameAsOwner ? ownerAddress || "" : undefined}
        />
        {errors.driver?.address && (
          <p className="text-red-500 text-sm">
            {errors.driver.address.message}
          </p>
        )}
      </div>

      <button
        onClick={onNext}
        className="bg-[#2D65F2] text-white p-3 rounded-md text-[14px] font-semibold w-full cursor-pointer"
      >
        Next
      </button>
    </div>
  );
};
export default OwnerDriverInfo;

const getShortCountryName = (name: string | null | undefined): string => {
  if (!name?.trim()) return "";

  const abbreviations: Record<string, string> = {
    // Very common & widely recognized
    "United States": "USA",
    "United Kingdom": "UK",
    "United Arab Emirates": "UAE",

    // Official short names or very common alternatives
    "Czech Republic": "Czechia", // Official short name since 2016
    "Bosnia and Herzegovina": "Bosnia", // Very common short form
    "Dominican Republic": "Dominican Rep.", // or "Dom. Rep."

    // Island/multi-word countries — often shortened in forms
    "Antigua and Barbuda": "Antigua & Barbuda", // or just "Antigua"
    "Trinidad and Tobago": "Trinidad & Tobago",
    "Saint Kitts and Nevis": "St. Kitts & Nevis",
    "Sao Tome and Principe": "São Tomé & Príncipe",
    "Central African Republic": "Central African Rep.",
    "Congo (DRC)": "DR Congo", // Very common distinction
    "Congo (Republic)": "Congo", // or "Rep. of Congo"

    // Others that appear occasionally
    "New Zealand": "NZ", // very common
    "South Africa": "South Africa", // rarely abbreviated
    "Costa Rica": "Costa Rica", // usually kept full
    "Saudi Arabia": "Saudi Arabia", // usually kept full
    "Hong Kong": "Hong Kong", // usually kept full (special case)
  };

  return abbreviations[name] || name;
};
